/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **********************************useRef****************************************/

import React, {FC, useEffect, useState, useRef} from 'react';
import open from '../../../utils/open';
import {injectIntl} from 'react-intl';
import {connect, useDispatch} from 'react-redux';
import {fetchMLAppRanking} from '../../actions/predictActions';
import AppHomeTab from '../../components/AppHomeTab/AppHomeTab';
import AppHomeContent from '../../components/AppHomeTab/AppHomeContent';
import RecentWork from '../../components/RecentWork/RecentWork';
import UIStore from '../../redux/UIStore';
import './AppHome.scss';
import {SET_CCPROJECTS_FINISHED, SET_CCPROJECTS_TIMEOUT_OR_ERROR} from '../../types';
import appHomeData from './appHomeData';
import ModalContainer from '@react/react-spectrum/ModalContainer';
import IllustratedMessage from '@react/react-spectrum/IllustratedMessage';
import Dialog from '@react/react-spectrum/Dialog';
import DocumentAccessService from '../../services/DocumentAccessService';
import Wait from '@react/react-spectrum/Wait';
import {getEnvironment} from '../../../utils/Environment';
import {CREATION_ERROR, API_ERROR, ENTITLEMENT_ERROR} from './contants';

import fetchAppHomeData from '../../hooks/fetchAppHomeData';
import fetchAppHomeBannerData from '../../hooks/fetchAppHomeBannerData';

type AppHomeProps = {
	locale: string;
	env: string;
	route: {
		routes: any[];
		path: string;
	};
	profileFetchFinished: boolean;
	customerSegment: string;
	storageQuota: number;
	fetchMLAppRanking: () => void;
	checkRecentDataStatus: symbol;
	planCode: string;
	intl: any;
	history: any;
	language: string;
	location: Object;
	leftRailOpen: boolean;
	setLeftRail: (newstate: boolean) => any;
};

const AppHome: FC<AppHomeProps> = (props) => {
	const {locale, env, route, fetchMLAppRanking, history, intl} = props;
	const dispatch = useDispatch();

	const sapCode = getSapCodeFromPath(route.path);

	const features = window.cchome.util.getAllFeatures();

	if (
		(!window.cchome.util.hasFeature('apphomes')
		|| (sapCode === 'FRED' && !features.includes('apphomes_canvas'))
		|| (sapCode === 'PHSP' && !features.includes('apphomes_photoshop_web'))
		|| (sapCode === 'ILST' && !features.includes('apphomes_illustrator')))
		|| (window.cchome.util.isIE())
	) {
		history.push('/cc');
	}

	if (features.includes('enable_logger')) {
		console.time('recent_new_cached');
		console.time('recent_new_uncached_start');
		console.time('recent_new_uncached_end');
	}

	const [showRecentsOnTop, setShowRecentsOnTop] = useState(true);

	const [ccProjectsClient, setCCProjectsClient] = useState<any | null>(null);
	const [canvasEntitlement, setCanvasEntitlement] = useState(false);

	useEffect(() => {
		const IMSData = {
			accountType: window.adobeIMS?.getUserProfile()?.account_type,
			displayName: window.adobeIMS?.getUserProfile()?.displayName,
			environment: getEnvironment() !== 'prod' ? 'stage' : 'prod',
			subscriptionType: window.adobeIMS?.getUserProfile()?.serviceAccounts && window.adobeIMS?.getUserProfile()?.serviceAccounts[0]?.serviceLevel || '',
			serviceLevel: window.adobeIMS?.getUserProfile()?.serviceAccounts && window.adobeIMS?.getUserProfile()?.serviceAccounts[0]?.serviceLevel || '',
			token: window.adobeIMS?.getAccessToken(),
			userEmail: window.adobeIMS?.getUserProfile()?.email,
			userId: window.adobeIMS?.getUserProfile()?.userId,
		};
		import(/* webpackChunkName: "ccProjects" */ '@ccx/projects')
			.then((ccProjectsLib) => {
				import(/* webpackChunkName: "documentPicker" */ '@ccx/ccx-document-picker').then(
					(ccxDocumentPickerLib) => {
						ccProjectsLib.sharedStore.updateStore({imsData: IMSData, systemLocale: locale, locale}).then(() => {
							const showPicker = ccxDocumentPickerLib.showPicker;
							try {
								setCCProjectsClient(
									new ccProjectsLib.ProjectsClient({
										locale: 'en',
										showPicker,
									}),
								);
							}
							catch (e) {
								canvasErrorModal(API_ERROR);
								dispatch({type: SET_CCPROJECTS_TIMEOUT_OR_ERROR});
							}
						});
					},
				);
			})
			.catch((e) => {
				console.error(e);
			});
	}, []);

	useEffect(() => {
		if (ccProjectsClient) {
			ccProjectsClient
				.checkCanvasCapability()
				.then((data) => {
					setCanvasEntitlement(data);
				})
				.then(() => {
					dispatch({type: SET_CCPROJECTS_FINISHED});
				});
		}
	}, [ccProjectsClient]);

	const canvasErrorModal = (type) => {
		const errorTitle = intl.formatMessage({id: `${appHomeData.FRED.errorModal[type].title}`});
		const errorDesc = intl.formatMessage({id: `${appHomeData.FRED.errorModal[type].description}`});
		const errorModal = (
			<Dialog
				autoFocusButton={null}
				backdropClickable={false}
				confirmDisabled={false}
				confirmLabel='OK'
				disableEscKey={false}
				isDismissible={false}
				keyboardConfirm={false}
				open
				role='dialog'
				title={errorTitle}
				trapFocus
				variant='error'
				id='canvas-error-dialog'
			>
				<span>{errorDesc}</span>
			</Dialog>
		);
		if (!document.querySelector('#canvas-error-dialog')) {
			ModalContainer.show(errorModal, this);
		}
	};

	const createSpaces = async() => {
		if (ccProjectsClient) {
			const rootElement = document.getElementById('root');
			if (!canvasEntitlement) {
				// throw up modal if user has no team
				canvasErrorModal(ENTITLEMENT_ERROR);
			}
			else {
				try {
					if (rootElement) {
						rootElement.classList.add('prevent-scrolling');
					}
					const canvasCreationPerformanceStart = performance.now();
					const canvasResponse = await ccProjectsClient.selectSpaceForCanvas();
					if (rootElement) {
						rootElement.classList.remove('prevent-scrolling');
					}
					if (canvasResponse instanceof Error) {
						// do nothing
					}
					else {
						window.cchome.util.logger.log('AppHome', 'leftnav: canvasresponse', canvasResponse);

						if (canvasResponse && (canvasResponse.spaceData || canvasResponse.teamData)) {
							let newDocument;
							if (canvasResponse.spaceData) {
								// Case: space exists
								const repoId
									= canvasResponse.spaceData && canvasResponse.spaceData['repo:repositoryId'];
								const assetId = canvasResponse.spaceData && canvasResponse.spaceData['repo:assetId'];
								newDocument = await new DocumentAccessService().createNewDocumentInSpace(
									assetId,
									repoId,
								);
							}
							else if (canvasResponse.teamData) {
								// Case: space doest not exist, create a default space
								const teamAssetId = canvasResponse.teamData && canvasResponse.teamData['repo:assetId'];
								const repoName = intl.formatMessage({id: 'appHomes.files.newCanvas.newSpace'});
								const canvasCreationDASCallStart = performance.now();
								newDocument = await new DocumentAccessService().createNewSpace(teamAssetId, repoName);
								const canvasCreationDASCallEnd = performance.now();
								window.cchome.util.logger.log(
									'AppHome',
									'New space canvas creation DAS call total time taken (milliseconds)',
									canvasCreationDASCallEnd - canvasCreationDASCallStart,
								);
							}

							if (newDocument instanceof Error) {
								// If there's an error returned from DAS, we should just
								// throw the error and let it be caught by error handling below
								throw newDocument;
							}
							else {
								// if adequate response, redirect using the link provided
								const canvasCreationPerformanceEnd = performance.now();
								window.cchome.util.logger.log(
									'AppHome',
									'New space canvas creation workflow total time taken (milliseconds)',
									canvasCreationPerformanceEnd - canvasCreationPerformanceStart,
								);
								open(newDocument, '_self');
								window.cchome.util.logger.log('AppHome', 'leftnav: newDocu response', newDocument);
							}
						}
					}
				}
				catch (error) {
					// throw up modal here
					canvasErrorModal(CREATION_ERROR);
					window.cchome.util.logger.log(
						'AppHome',
						'leftnav: ccProjectsClient threw error for new canvas',
						error,
					);
				}
			}
		}
		else {
			// some error, do toast here or something
			canvasErrorModal(CREATION_ERROR);
			window.cchome.util.logger.log('AppHome', 'leftnav: ccProjectsClient not initialized');
		}
	};

	useEffect(() => {
		if (features.includes('20190412_Home_ML')) {
			fetchMLAppRanking();
		}
	}, [features]);

	if (!features.length) {return null;}

	const handleRouteChange = (route: string) => {
		history.push(route);
	};

	const shiftRecentsToBottom = () => {
		setShowRecentsOnTop(false);
	};

	const hasLeftRail = features.includes('apphomes');

	const isOpen = hasLeftRail && props.leftRailOpen && document.body.clientWidth >= 960;

	const recent = (
		<RecentWork
			key='recent-container'
			sapcode={sapCode}
			env={env}
			shiftRecentsToBottom={shiftRecentsToBottom}
			locale={locale}
			isleftNavOpen={isOpen}
			createSpacesCallback={() => createSpaces()}
		/>
	);

	// Fetch the data to load App Homes.
	let appContextType;
	switch (sapCode) {
		case 'FRED':
			appContextType = 'canvas';
			break;
		case 'PHSP':
			appContextType = 'photoshopweb';
			break;
		case 'ILST':
			appContextType = 'illustratorweb';
			break;
		default:
			appContextType = 'canvas';
	}

	const appHomesState = fetchAppHomeData(appContextType);
	const appHomesBannerState = fetchAppHomeBannerData(appContextType, locale);

	let appHome = <div />; // MUST return something here
	if (window.cchome.util.hasFeature('apphomes_ucs_data')) {
		if (appHomesState.status === 'pending') {
			appHome = (
				<div className={`app-home-container${isOpen ? ' leftnav-open loading' : ' loading'}`}>
					<Wait centered size='L' />
				</div>
			);
		}
		else if (appHomesState.status === 'rejected') {
			const errorHeading = intl.formatMessage({id: 'app.errorpage.generic.title'});
			const errorDesc = intl.formatMessage({id: 'app.errorpage.generic.detail'});
			appHome = (
				<div className={`app-home-container${isOpen ? ' leftnav-open loading' : ' loading'}`}>
					<IllustratedMessage
						description={errorDesc}
						heading={errorHeading}
						illustration={
							<svg height='103' viewBox='0 0 150 103' width='150'>
								<path d='M133.7,8.5h-118c-1.9,0-3.5,1.6-3.5,3.5v27c0,0.8,0.7,1.5,1.5,1.5s1.5-0.7,1.5-1.5V23.5h119V92c0,0.3-0.2,0.5-0.5,0.5h-118c-0.3,0-0.5-0.2-0.5-0.5V69c0-0.8-0.7-1.5-1.5-1.5s-1.5,0.7-1.5,1.5v23c0,1.9,1.6,3.5,3.5,3.5h118c1.9,0,3.5-1.6,3.5-3.5V12C137.2,10.1,135.6,8.5,133.7,8.5z M15.2,21.5V12c0-0.3,0.2-0.5,0.5-0.5h118c0.3,0,0.5,0.2,0.5,0.5v9.5H15.2z M32.6,16.5c0,0.6-0.4,1-1,1h-10c-0.6,0-1-0.4-1-1s0.4-1,1-1h10C32.2,15.5,32.6,15.9,32.6,16.5z M13.6,56.1l-8.6,8.5C4.8,65,4.4,65.1,4,65.1c-0.4,0-0.8-0.1-1.1-0.4c-0.6-0.6-0.6-1.5,0-2.1l8.6-8.5l-8.6-8.5c-0.6-0.6-0.6-1.5,0-2.1c0.6-0.6,1.5-0.6,2.1,0l8.6,8.5l8.6-8.5c0.6-0.6,1.5-0.6,2.1,0c0.6,0.6,0.6,1.5,0,2.1L15.8,54l8.6,8.5c0.6,0.6,0.6,1.5,0,2.1c-0.3,0.3-0.7,0.4-1.1,0.4c-0.4,0-0.8-0.1-1.1-0.4L13.6,56.1z' />
							</svg>
						}
					/>
				</div>
			);
		}
		else if (appHomesState.status === 'fulfilled') {
			let bannerData;
			if (appHomesBannerState.status === 'pending') {
				bannerData = null;
			}
			else if (appHomesBannerState.status === 'rejected') {
				bannerData = null;
			}
			else if (appHomesBannerState.status === 'fulfilled') {
				bannerData = appHomesBannerState.value;
			}

			appHome = (
				<>
					<div className={`app-home-container${isOpen ? ' leftnav-open' : ''}`}>
						<AppHomeContent
							bannerData={bannerData}
							ucsAppHomeData={appHomesState.value}
							leftRailOpen={isOpen}
							recent={recent}
							intl={intl}
							createSpacesCallback={() => createSpaces()}
						/>
					</div>
				</>
			);
		}
	}
	else {
		appHome = (
			<>
				<div className={`app-home-container${isOpen ? ' leftnav-open' : ''}`}>
					{/* <Route path="/webapps">
						<AppHomeTab appHomeData={appHomeData[sapCode]} leftRailOpen={isOpen} recent={recent} />
					</Route> */}
					<AppHomeTab
						appHomeData={appHomeData[sapCode]}
						leftRailOpen={isOpen}
						recent={recent}
						intl={intl}
						createSpacesCallback={() => createSpaces()}
					/>
				</div>
			</>
		);
	}

	return appHome;
};

const getSapCodeFromPath = (path: string) => {
	const name = path.split('/')[2];
	switch (name) {
		case 'canvas':
			return 'FRED';
		case 'photoshop':
			return 'PHSP';
		case 'illustrator':
			return 'ILST';
	}
	throw new Error('path does not include app name.');
};

const mapStateToProps = state => ({
	customerSegment: state.profile.subscriptionInfo.customerSegment,
	storageQuota: state.profile.storage.storage_quota,
	predictAppRanking: state.predict.predictAppRanking,
	profileFetchFinished: state.profile.profileFetchFinished,
	planCode: state.profile.subscriptionInfo.planCode,
	language: state.app.language,
	leftRailOpen: state.uistore.leftRailOpen,
});

const mapDispatchToProps = {
	fetchMLAppRanking,
	setLeftRail: UIStore.setLeftRail,
};

export default {
	component: connect(mapStateToProps, mapDispatchToProps)(injectIntl(AppHome)),
};
export type {AppHomeProps};
